import React, { useState, useEffect } from 'react';
import { Check, CreditCard, AlertCircle, X, Upload, ArrowLeft, ArrowRight, ShieldCheck } from 'lucide-react';
import { useApp } from '../hooks/useApp';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import PageMeta from '../components/common/PageMeta';
import { createOrder, uploadReceipt } from '../services/supabase/orders';
import { getSettings } from '../services/supabase/settings';
import { OTHER_VALUE, UZBEKISTAN_REGIONS, getCitiesForRegion } from '../data/uzbekistanDelivery';
import { supabase } from '../supabaseClient';
import { getOrderSource } from '../utils/siteMode';

const countryCitiesFlat = {
    uzbekistan: ['tashkent', 'samarkand', 'bukhara', 'andijan', 'namangan', 'fergana', 'nukus', 'karshi'],
    kazakhstan: ['astana', 'almaty', 'shymkent'],
    kyrgyzstan: ['bishkek', 'osh'],
    tajikistan: ['dushanbe', 'khujand'],
    turkmenistan: ['ashgabat'],
    turkey: ['istanbul', 'ankara'],
    uae: ['dubai', 'abu_dhabi'],
    russia: ['moscow', 'saint_petersburg']
};

const CheckoutPage = () => {
    const { cart, getTotalPrice, clearCart, setCurrentPage, currentUser } = useApp();
    const { language, t, translateColor } = useLanguage();
    const { isAdmin } = useAuth();
    const [paymentMethod, setPaymentMethod] = useState('humo');
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [receiptFile, setReceiptFile] = useState(null);
    const [settings, setSettings] = useState(null);
    const [formData, setFormData] = useState({
        name: currentUser?.name || '',
        phone: currentUser?.phone || '',
        address: '',
        region: '',
        city: '',
        regionManual: '',
        cityManual: '',
        notes: ''
    });
    const [fieldErrors, setFieldErrors] = useState({});
    /** `customers.country` JWT da bo‘lmasa ham yetkazib berish rejimini to‘g‘ri tanlash */
    const [profileCountryFromDb, setProfileCountryFromDb] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            // ... existing code ...
            const result = await getSettings();
            if (result.success) {
                setSettings(result.settings);
            }
        };
        fetchSettings();
    }, []);

    /** Sessiya kechiksa yoki ma'lumot `customers`da bo'lsa — formani to'ldirish (birinchi render bo'sh qolmasin) */
    useEffect(() => {
        if (!currentUser?.id) {
            setProfileCountryFromDb(null);
            return;
        }
        setProfileCountryFromDb(null);
        let cancelled = false;

        const mergeFromProfile = (patch) => {
            if (cancelled) return;
            setFormData((prev) => ({
                ...prev,
                name: prev.name.trim() ? prev.name : (patch.name || ''),
                phone: prev.phone.trim() ? prev.phone : (patch.phone || ''),
                address: prev.address.trim() ? prev.address : (patch.address || ''),
            }));
        };

        mergeFromProfile({
            name: currentUser.name || '',
            phone: currentUser.phone || '',
            address: '',
        });

        (async () => {
            try {
                let data = null;
                const selFull = 'name, phone, address, country';
                const selBasic = 'name, phone, address';
                const fetchCustomer = async (col, val) => {
                    let r = await supabase.from('customers').select(selFull).eq(col, val).maybeSingle();
                    if (r.error && /column|country|schema/i.test(String(r.error.message || ''))) {
                        r = await supabase.from('customers').select(selBasic).eq(col, val).maybeSingle();
                    }
                    return r;
                };
                const byId = await fetchCustomer('id', currentUser.id);
                if (!byId.error) data = byId.data;
                if (!data && currentUser.email) {
                    const byEmail = await fetchCustomer('email', currentUser.email);
                    if (!byEmail.error) data = byEmail.data;
                }
                if (cancelled || !data) return;
                if (data.country != null && String(data.country).trim() !== '') {
                    setProfileCountryFromDb(String(data.country).trim().toLowerCase());
                }
                mergeFromProfile({
                    name: data.name || currentUser.name || '',
                    phone: data.phone || currentUser.phone || '',
                    address: data.address || '',
                });
            } catch {
                /* ignore */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentUser?.id, currentUser?.email, currentUser?.name, currentUser?.phone]);

    /** Admin uchun checkout ko‘rinmasin — savatdagi tezkor buyurtmaga yo‘naltiramiz */
    useEffect(() => {
        if (!currentUser || !isAdmin) return;
        setCurrentPage('cart');
    }, [currentUser, isAdmin, setCurrentPage]);

    const userCountry = (currentUser?.country || profileCountryFromDb || 'uzbekistan').toLowerCase();
    const isUzbekistan = userCountry === 'uzbekistan';

    const buildDeliveryAddressLine = () => {
        if (!isUzbekistan) {
            const cityLabel = formData.city ? t(formData.city) : '';
            return `${cityLabel}, ${formData.address}`.trim();
        }
        if (formData.region === OTHER_VALUE) {
            const head = [formData.regionManual, formData.cityManual].filter((s) => s && String(s).trim()).join(', ');
            return `${head}; ${formData.address}`;
        }
        const regionLabel = formData.region ? t(formData.region) : '';
        if (formData.city === OTHER_VALUE) {
            return `${regionLabel}, ${String(formData.cityManual || '').trim()}; ${formData.address}`;
        }
        const cityLabel = formData.city ? t(formData.city) : '';
        return `${regionLabel}, ${cityLabel}; ${formData.address}`;
    };

    const validateForm = () => {
        const errs = {};
        if (!formData.name.trim()) errs.name = t('nameRequired');
        else if (formData.name.trim().length < 2) errs.name = t('nameMinLength');
        if (!formData.phone.trim()) errs.phone = t('phoneRequired');
        else if (formData.phone.replace(/\D/g, '').length < 9) errs.phone = t('phoneMinDigits');
        if (!formData.address.trim()) errs.address = t('addressRequired');
        else if (formData.address.trim().length < 5) errs.address = t('addressMinLength');

        if (isUzbekistan) {
            if (!formData.region) errs.region = t('regionRequired');
            else if (formData.region === OTHER_VALUE) {
                if (!formData.regionManual.trim() || formData.regionManual.trim().length < 2) errs.regionManual = t('regionManualRequired');
                if (!formData.cityManual.trim() || formData.cityManual.trim().length < 2) errs.cityManual = t('cityManualRequired');
            } else {
                if (!formData.city) errs.city = t('cityRequired');
                else if (formData.city === OTHER_VALUE && (!formData.cityManual.trim() || formData.cityManual.trim().length < 2)) {
                    errs.cityManual = t('cityManualRequired');
                }
            }
        } else {
            if (!formData.city.trim()) errs.city = t('cityRequired');
        }

        if (!receiptFile) errs.receipt = t('receiptRequired');
        setFieldErrors(errs);
        setError(Object.values(errs)[0] || '');
        return Object.keys(errs).length === 0;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFieldErrors(prev => ({ ...prev, receipt: '' }));
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError(t('maxFileSize'));
                return;
            }
            setReceiptFile(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        if (!validateForm()) return;

        setLoading(true);
        try {
            const orderData = {
                userId: currentUser?.id || 'guest',
                customerInfo: {
                    ...formData,
                    email: currentUser?.email || '',
                    address: buildDeliveryAddressLine()
                },
                products: cart.map(item => ({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    image: item.images?.[0] || item.model_3d_url || '',
                    color: item.selectedColor || item.color,
                    size: item.size
                })),
                totalPrice: getTotalPrice(),
                status: 'new',
                payment_status: 'unpaid',
                paymentMethod: paymentMethod,
                paymentMethodDetail: settings?.[`${paymentMethod}_card`] || '',
                language: language,
                createdAt: new Date(),
                source: getOrderSource(),
            };

            const orderResult = await createOrder(orderData);
            if (!orderResult.success) throw new Error(orderResult.error || t('orderCreationFailed'));

            const orderId = orderResult.orderId;

            // Upload receipt if provided
            if (receiptFile) {
                const uploadResult = await uploadReceipt(orderId, receiptFile);

                if (!uploadResult.success) {
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.error('Receipt upload failed:', uploadResult.error);
                    }
                    throw new Error(`Order created but receipt upload failed: ${uploadResult.error}`);
                }
            }

            setOrderSuccess(true);
            clearCart();
            setTimeout(() => setCurrentPage('home'), 4000);

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error('Checkout error:', err);
            }
            setError(err.message || t('unknownError'));
        } finally {
            setLoading(false);
        }
    };

    const getCardNumber = (method) => {
        if (!settings) return '...';
        return settings[`${method}_card`] || t('cardNumberNotSet');
    };

    if (currentUser && isAdmin) {
        return (
            <>
                <PageMeta title={t('cart')} description={t('metaDescCart')} siteName={settings?.site_name} />
                <div className="min-h-[45vh] flex flex-col items-center justify-center px-4 text-center">
                    <p className="text-gray-600 max-w-md">{t('adminCheckoutRedirectMessage')}</p>
                </div>
            </>
        );
    }

    if (orderSuccess) {
        return (
            <>
                <PageMeta title={t('orderSuccess')} description={t('metaDescCheckout')} siteName={settings?.site_name} />
                <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 max-w-2xl mx-auto">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-fade-in">
                    <Check className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-3xl font-display font-bold mb-4 text-gray-900">{t('orderSuccess') || 'Order Successful!'}</h2>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                    {t('orderSuccessMsg')}
                </p>
                <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                    <div className="bg-primary h-full w-full animate-[shimmer_2s_infinite]"></div>
                </div>
                <p className="text-sm text-gray-400 mt-2">{t('redirecting')}...</p>
            </div>
            </>
        );
    }

    const getAvailableCitiesFlat = () => {
        return countryCitiesFlat[userCountry] || countryCitiesFlat.uzbekistan;
    };

    return (
        <>
            <PageMeta title={t('checkout')} description={t('metaDescCheckout')} siteName={settings?.site_name} />
            <div className="container mx-auto px-4 md:px-6 py-8">
            <button
                onClick={() => setCurrentPage('cart')}
                className="flex items-center text-gray-500 hover:text-primary mb-6 transition-colors font-medium"
            >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('backToCart') || 'Back to Cart'}
            </button>

            <h1 className="text-3xl md:text-4xl font-display font-bold mb-8 text-gray-900">{t('checkout')}</h1>

            {error && (
                <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3 animate-fade-in">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="font-bold text-red-800">{t('error')}</h3>
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                    <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Section 1: Personal Info */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                            <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center">
                                <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</span>
                                {t('personalInfo')}
                            </h3>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">{t('name')} *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setFieldErrors(prev => ({ ...prev, name: '' })); }}
                                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.name ? 'border-red-400' : 'border-gray-200'}`}
                                    />
                                    {fieldErrors.name && <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">{t('phone')} *</label>
                                    <input
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={(e) => { setFormData({ ...formData, phone: e.target.value }); setFieldErrors(prev => ({ ...prev, phone: '' })); }}
                                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.phone ? 'border-red-400' : 'border-gray-200'}`}
                                        placeholder="+998"
                                    />
                                    {fieldErrors.phone && <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Delivery — O'zbekiston: viloyat → shahar/tuman; «Boshqa» + qo'lda */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                            <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center">
                                <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</span>
                                {t('deliveryInfo')}
                            </h3>
                            <div className="grid md:grid-cols-2 gap-6">
                                <p className="md:col-span-2 text-sm text-gray-500 -mt-2">{t('deliveryLocationHint')}</p>

                                {isUzbekistan ? (
                                    <>
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">{t('selectRegion')} *</label>
                                            <select
                                                value={formData.region}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        region: v,
                                                        city: '',
                                                        cityManual: '',
                                                        regionManual: v === OTHER_VALUE ? prev.regionManual : ''
                                                    }));
                                                    setFieldErrors((prev) => ({ ...prev, region: '', regionManual: '', cityManual: '', city: '' }));
                                                }}
                                                className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.region ? 'border-red-400' : 'border-gray-200'}`}
                                            >
                                                <option value="">{t('selectRegion')}</option>
                                                {UZBEKISTAN_REGIONS.map((r) => (
                                                    <option key={r.id} value={r.id}>{t(r.id)}</option>
                                                ))}
                                                <option value={OTHER_VALUE}>{t('locationOther')}</option>
                                            </select>
                                            {fieldErrors.region && <p className="mt-1 text-sm text-red-600">{fieldErrors.region}</p>}
                                        </div>

                                        {formData.region && formData.region !== OTHER_VALUE && (
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('cityDistrict')} *</label>
                                                <select
                                                    value={formData.city}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        setFormData((prev) => ({ ...prev, city: v, cityManual: v === OTHER_VALUE ? prev.cityManual : '' }));
                                                        setFieldErrors((prev) => ({ ...prev, city: '', cityManual: '' }));
                                                    }}
                                                    className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.city ? 'border-red-400' : 'border-gray-200'}`}
                                                >
                                                    <option value="">{t('selectCity')}</option>
                                                    {getCitiesForRegion(formData.region).map((cityKey) => (
                                                        <option key={cityKey} value={cityKey}>{t(cityKey)}</option>
                                                    ))}
                                                    <option value={OTHER_VALUE}>{t('locationOther')}</option>
                                                </select>
                                                {fieldErrors.city && <p className="mt-1 text-sm text-red-600">{fieldErrors.city}</p>}
                                            </div>
                                        )}

                                        {formData.region === OTHER_VALUE && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">{t('specifyRegionManual')} *</label>
                                                    <input
                                                        type="text"
                                                        value={formData.regionManual}
                                                        onChange={(e) => { setFormData({ ...formData, regionManual: e.target.value }); setFieldErrors((prev) => ({ ...prev, regionManual: '' })); }}
                                                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.regionManual ? 'border-red-400' : 'border-gray-200'}`}
                                                    />
                                                    {fieldErrors.regionManual && <p className="mt-1 text-sm text-red-600">{fieldErrors.regionManual}</p>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">{t('specifyCityManual')} *</label>
                                                    <input
                                                        type="text"
                                                        value={formData.cityManual}
                                                        onChange={(e) => { setFormData({ ...formData, cityManual: e.target.value }); setFieldErrors((prev) => ({ ...prev, cityManual: '' })); }}
                                                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.cityManual ? 'border-red-400' : 'border-gray-200'}`}
                                                    />
                                                    {fieldErrors.cityManual && <p className="mt-1 text-sm text-red-600">{fieldErrors.cityManual}</p>}
                                                </div>
                                            </>
                                        )}

                                        {formData.region && formData.region !== OTHER_VALUE && formData.city === OTHER_VALUE && (
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('specifyCityManual')} *</label>
                                                <input
                                                    type="text"
                                                    value={formData.cityManual}
                                                    onChange={(e) => { setFormData({ ...formData, cityManual: e.target.value }); setFieldErrors((prev) => ({ ...prev, cityManual: '' })); }}
                                                    className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.cityManual ? 'border-red-400' : 'border-gray-200'}`}
                                                    placeholder={t('cityDistrict')}
                                                />
                                                {fieldErrors.cityManual && <p className="mt-1 text-sm text-red-600">{fieldErrors.cityManual}</p>}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">{t('city')} *</label>
                                        <select
                                            value={formData.city}
                                            onChange={(e) => { setFormData({ ...formData, city: e.target.value }); setFieldErrors((prev) => ({ ...prev, city: '' })); }}
                                            className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.city ? 'border-red-400' : 'border-gray-200'}`}
                                        >
                                            <option value="">{t('selectCity')}</option>
                                            {getAvailableCitiesFlat().map((cityKey) => (
                                                <option key={cityKey} value={cityKey}>{t(cityKey)}</option>
                                            ))}
                                        </select>
                                        {fieldErrors.city && <p className="mt-1 text-sm text-red-600">{fieldErrors.city}</p>}
                                    </div>
                                )}

                                <div className={isUzbekistan ? 'md:col-span-2' : ''}>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">{t('address')} *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.address}
                                        onChange={(e) => { setFormData({ ...formData, address: e.target.value }); setFieldErrors((prev) => ({ ...prev, address: '' })); }}
                                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${fieldErrors.address ? 'border-red-400' : 'border-gray-200'}`}
                                    />
                                    {fieldErrors.address && <p className="mt-1 text-sm text-red-600">{fieldErrors.address}</p>}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-2">{t('notes')}</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                        rows="2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Payment */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                            <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center">
                                <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">3</span>
                                {t('paymentMethod')}
                            </h3>

                            <div className="grid grid-cols-3 gap-4 mb-8">
                                {['humo', 'uzcard', 'visa'].map(method => (
                                    <label key={method} className={`relative flex flex-col items-center justify-center p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === method
                                        ? 'border-primary bg-primary/5 shadow-md'
                                        : 'border-gray-200 hover:border-primary/50'
                                        }`}>
                                        <input
                                            type="radio"
                                            name="payment"
                                            value={method}
                                            checked={paymentMethod === method}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="absolute opacity-0"
                                        />
                                        <span className="font-bold uppercase text-sm mb-1">{method}</span>
                                        {paymentMethod === method && <Check className="w-4 h-4 text-primary absolute top-2 right-2" />}
                                    </label>
                                ))}
                            </div>

                            <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Card Number</span>
                                    <button
                                        type="button"
                                        className="text-primary text-sm font-bold hover:underline"
                                        onClick={() => navigator.clipboard.writeText(getCardNumber(paymentMethod))}
                                    >
                                        Copy
                                    </button>
                                </div>
                                <div className="font-mono text-xl md:text-2xl text-gray-800 tracking-widest break-all">
                                    {getCardNumber(paymentMethod)}
                                </div>
                            </div>

                            <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${fieldErrors.receipt ? 'border-red-400 bg-red-50/30' : receiptFile ? 'border-green-500 bg-green-50/50' : 'border-gray-300 hover:border-primary bg-gray-50'}`}>
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="receipt-upload"
                                />
                                <label htmlFor="receipt-upload" className="cursor-pointer flex flex-col items-center">
                                    {receiptFile ? (
                                        <>
                                            <Check className="w-10 h-10 text-green-500 mb-2" />
                                            <span className="font-bold text-green-700">{receiptFile.name}</span>
                                            <span className="text-sm text-green-600">Click to change</span>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-10 h-10 text-gray-400 mb-2" />
                                            <span className="font-bold text-gray-700">Upload Payment Receipt</span>
                                            <span className="text-sm text-gray-500">Screenshot or PDF (Max 5MB)</span>
                                        </>
                                    )}
                                </label>
                            </div>
                            {fieldErrors.receipt && <p className="mt-2 text-sm text-red-600">{fieldErrors.receipt}</p>}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center space-x-2 transition-all ${loading || !receiptFile
                                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                : 'bg-primary hover:bg-primary-dark text-white hover:translate-y-[-2px] hover:shadow-xl'
                                }`}
                        >
                            {loading ? (
                                <span>Processing...</span>
                            ) : (
                                <>
                                    <span>Confirm Order</span>
                                    <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Summary Sidebar */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24 border border-gray-100">
                        <h3 className="font-bold text-lg mb-6 text-gray-900 pb-4 border-b border-gray-100 flex items-center">
                            <CreditCard className="w-5 h-5 mr-2 text-primary" />
                            {t('orderSummary')}
                        </h3>
                        <div className="space-y-4 mb-6 custom-scrollbar max-h-96 overflow-y-auto">
                            {cart.map(item => (
                                <div key={item.id} className="flex gap-3">
                                    {(item.images && item.images.length > 0) ? (
                                        <img
                                            src={item.images[0]}
                                            className="w-14 h-14 object-cover rounded-md bg-gray-50"
                                            alt=""
                                        />
                                    ) : item.model_3d_url ? (
                                        <div className="w-14 h-14 rounded-md bg-gray-50 overflow-hidden">
                                            <model-viewer
                                                src={item.model_3d_url}
                                                auto-rotate
                                                interaction-prompt="none"
                                                style={{ width: '100%', height: '100%', backgroundColor: '#f9fafb' }}
                                            ></model-viewer>
                                        </div>
                                    ) : (
                                        <div className="w-14 h-14 rounded-md bg-gray-100 flex items-center justify-center text-[8px] text-gray-400 font-bold uppercase text-center p-1">
                                            No Img
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <div className="text-sm font-bold text-gray-900 line-clamp-1">{item.name[language]}</div>
                                        <div className="text-xs text-gray-500">
                                            ${item.price?.toLocaleString()} × {item.quantity}
                                            {item.is_kg ? ' kg' : ''}
                                            {(item.selectedColor || item.color) && (
                                                <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-secondary font-bold uppercase tracking-tighter">
                                                    {translateColor(item.selectedColor || item.color)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="pt-4 border-t border-gray-100 space-y-2">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Subtotal</span>
                                <span>${getTotalPrice().toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm text-green-600 font-bold">
                                <span>Shipping</span>
                                <span>Free</span>
                            </div>
                            <div className="flex justify-between text-xl font-black text-primary pt-2 mt-2 border-t border-gray-100">
                                <span>Total</span>
                                <span>${getTotalPrice().toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                            <ShieldCheck className="w-4 h-4 text-primary" />
                            Secure SSL Encryption
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default CheckoutPage;
